import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { base64ToFile, ImageCroppedEvent } from 'ngx-image-cropper';
import { finalize } from 'rxjs/operators';
import { CountriesService } from 'src/app/global/countries-list';
import { Country } from 'src/app/models/country.model';
import { SetupRequest } from 'src/app/models/setup-request.model';
import { User } from 'src/app/models/user.model';
import { AlertService } from 'src/app/service/alert.service';
import { DisordersTranslationService } from 'src/app/service/disorders-translation.service';
import { AvatarUploadServiceService } from 'src/app/service/http-services/avatar-upload-service.service';
import { DisordersService } from 'src/app/service/http-services/disorders.service';
import { UserService } from 'src/app/service/http-services/user.service';
import { runInThisContext } from 'vm';
import { DisordersSetup } from '../user-setup/user-setup.component';

@Component({
  selector: 'app-profile',
  templateUrl: './profile.component.html',
  styleUrls: ['./profile.component.css']
})
export class ProfileComponent implements OnInit {

  user!: User;
  form!: FormGroup;
  disorders: any[] = []
  countries!: Country[]

  editAvatar: boolean = false;
  editUser: boolean = false;
  editDisorders: boolean = false;
  oldDisorders: string[] = [];

  showConfrimChange: boolean = false;
  confrimReq: any;
  showUserDataEditError: boolean = false;

  file!: any
  isAvatarLoading: boolean = false;
  imageChangedEvent: any = '';
  croppedImage: any;
  showCropper: boolean = false;
  avatarUrl!: string

  deleteAccountPopup: boolean = false;

  @ViewChild('statusInput') private statusInput!: ElementRef;
  editStatusMessage:boolean = false;

  constructor(
    private fb: FormBuilder,
    private userService: UserService,
    private disordersTranslationService: DisordersTranslationService,
    private disordersService: DisordersService,
    private countriesService: CountriesService,
    private alertService: AlertService,
    private router: Router,
    private avatarService: AvatarUploadServiceService
  ) { }

  ngOnInit(): void {
    this.user = this.userService.getUserStorage();
    this.form = this.fb.group({
      name: [this.user.name, [Validators.required, Validators.pattern('^[a-zA-Z]*$')]],
      email: [this.user.email, [Validators.required, Validators.email]],
      country: [this.user.country, [Validators.required]]
    })
    this.avatarUrl = this.getAvatar(this.user.avatarId);
    this.countries = this.countriesService.getCountries();
    this.disordersService.getAllUserDisorders().subscribe(disorders => {
      this.disorders = this.disordersTranslationService.translateDisordersEnum(disorders)
    })
  }

  translateCountry(code: string): string {
    return this.countriesService.getCountries().find(x => x.value === code)!.text;
  }

  getAvatar(id: string): string {
    const baseUrl = "https://getbetter-avatars.s3-eu-west-1.amazonaws.com/";
    if (id !== null) {
      return baseUrl + id
    }
    else {
      return "https://i1.wp.com/www.mnleadership.org/wp-content/uploads/2017/02/Anonymous-Avatar.png?ssl=1"
    }
  }

  onEditDisorders() {
    if (!this.editDisorders) {
      let disordersSetup: DisordersSetup[] = [];
      this.oldDisorders = this.disorders;
      this.editDisorders = true;
      this.disordersService.getEnumDisorders().subscribe(rsp => {
        rsp.forEach(x => {
          let disorder = new DisordersSetup(x.key, x.value);
          disorder.isSelected = this.oldDisorders.includes(x.value);
          disordersSetup.push(disorder);
        })
        this.disorders = disordersSetup;
      })
    }
    else {
      const markedDisorders = this.disorders.filter(x => x.isSelected);
      if (JSON.stringify(this.oldDisorders) === JSON.stringify(markedDisorders.map(x => x.value))) {
        this.disorders = this.oldDisorders;
        this.editDisorders = false;
      }
      else {
        this.disordersService.saveDisorders(new SetupRequest(markedDisorders.map(x => x.key))).subscribe(rsp => {
          this.disorders = this.disordersTranslationService.translateDisordersEnum(rsp);
          this.editDisorders = false;
        })
      }
    }
  }

  onEditUser() {
    //closeEditMode
    if (this.editUser) {
      const updateRequest = {
        name: this.name.value,
        email: this.email.value,
        country: this.country.value
      }
      let user = this.userService.getUserStorage();
      const oldUser = {
        name: user.name,
        email: user.email,
        country: user.country
      }
      if (JSON.stringify(updateRequest) !== JSON.stringify(oldUser)) {
        if(this.form.valid){
        if (updateRequest.email !== oldUser.email) {
          this.confrimReq = updateRequest;
          this.showConfrimChange = true;
        }
        else {
          this.userService.updateUser(updateRequest).subscribe(rsp => {
            this.userService.saveUserStorage(rsp);
            this.user = rsp;
            this.editUser = false;
            this.form.disable();
          })
        }
      }
    }
      //disable form if no changes
      else{
        this.editUser = false;
        this.form.disable();
      }
    }
    //enable edit
    else {
      this.editUser = true;
      this.form.enable();
    }
  }


  getDisorderValue(d: any) {
    if (d.value) {
      return d.value;
    }
    else {
      return d;
    }
  }

  mark(disorder: any) {
    if (this.editDisorders) {
      let d = this.disorders.find(x => x === disorder);
      if (this.disorders.filter(x => x.isSelected).length === 1 && d.isSelected) {
        this.alertService.error("At least 1 disorder required")
      }
      else {
        d.isSelected = !d.isSelected
      }
    }
  }

  confirmChange(){
    this.userService.updateUser(this.confrimReq).subscribe(() => {
      this.router.navigate(["/logout"])
    },
    err => {
      this.alertService.error(err.error.message);
      this.cancelChange();
    })
  }

  cancelChange(){
    this.name.setValue(this.user.name);
    this.email.setValue(this.user.email);
    this.country.setValue(this.user.country);
    this.form.markAsPristine();
    this.editUser = false;
    this.form.disable()
    this.showConfrimChange = false;
  }

  handleFileInput(file: any) {
    this.imageChangedEvent = file;
    this.showCropper = true;
  }

  uploadAvatar() {
    this.isAvatarLoading = true;
    this.avatarService.uploadAvatar(this.file).pipe(
      finalize(() => {
        this.isAvatarLoading = false
        this.editAvatar = false;
      })
    )
      .subscribe(rsp => {
        let userWithAvatar = this.userService.getUserStorage();
        userWithAvatar.avatarId = rsp;
        this.userService.saveUserStorage(userWithAvatar);
        this.avatarUrl = this.getAvatar(userWithAvatar.avatarId) + "?n";
      });
  }

  imageCropped(event: ImageCroppedEvent){
    this.croppedImage = event.base64;
    this.file = new File([base64ToFile(this.croppedImage)], "avatar.jpeg");
  }

  confirmDelete(){
    this.userService.deleteUser().subscribe(() => {
      this.router.navigate(["/logout"])
    })
  }

  onEditStatusMessage(){
    if(!this.editStatusMessage){
      this.editStatusMessage = true;
    }
    else {
      let status:string = this.statusInput.nativeElement.value;
      if(status === this.user.statusMessage){
        this.editStatusMessage = false;
      }
      else{
        this.userService.setStatusMessage(status).subscribe(() => {
          this.user.statusMessage = status;
          this.userService.saveUserStorage(this.user);
          this.editStatusMessage = false;
        },
        err => {
          let errBody = JSON.parse(err.error);
          this.alertService.error(errBody.message)
        })
      }
    }
  }


  get name() {
    return this.form.controls.name;
  }

  get email() {
    return this.form.controls.email;
  }

  get country() {
    return this.form.controls.country;
  }

}
